import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card } from './Card';
import { cn } from './cn';

/**
 * A page that could not load.
 *
 * Proportioned like `EmptyState` — an 11-unit disc with a 5-unit glyph — so a
 * failure is the same size as an empty list rather than louder than one.
 *
 * This is the whole-page state. The product's inline retry affordance, the one
 * that sits inside a section that failed while the rest of the screen still
 * works, stays in the app: it needs translated copy and a refetch.
 */

export interface ErrorStateProps {
  title: string;
  message?: string;
  action?: ReactNode;
  className?: string;
}

export function ErrorState({ title, message, action, className }: ErrorStateProps) {
  return (
    <Card
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className
      )}
    >
      <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10">
        <AlertCircle className="h-5 w-5 text-destructive" />
      </div>
      <div className="space-y-1">
        <p className="font-medium text-foreground">{title}</p>
        {message ? (
          <p className="mx-auto max-w-sm text-sm text-muted-foreground">{message}</p>
        ) : null}
      </div>
      {action}
    </Card>
  );
}
